/**
 * Contract for custom window events dispatched between the shell and micro-frontends.
 * All event names use the prefix 'dashboard:' to avoid collisions with browser or
 * third-party events.
 *
 * ******* DO NOT MAKE BACKWARDS INCOMPATIBLE CHANGES *******
 * Shell and apps deploy independently. Renaming or removing an event constant
 * can silently break listeners or emitters that have not yet been redeployed.
 *
 * Safe changes:
 *   - Add a new event constant
 *   - Add a JSDoc deprecated note and keep the event for one release cycle
 *
 * Unsafe changes:
 *   - Remove or rename an event constant
 *
 * Who emits / who listens is documented on each event below.
 */
// ---------------------------------------------------------------------------
// Event name constants
// ---------------------------------------------------------------------------
export var GLOBAL_SHELL_EVENTS = {
    /**
     * Fired by the **setup app** whenever onboarding step completion changes —
     * both on initial data load and after any mark-done / mark-undone mutation.
     * The **shell** listens to this event and refetches its own GQL query to
     * update the "Getting Started" nav link badge and hide the link once all
     * steps are complete.
     */
    ONBOARDING_STEPS_UPDATED: 'setup:onboarding-steps-updated',
};
// ---------------------------------------------------------------------------
// Shared onboarding constants
// ---------------------------------------------------------------------------
/**
 * The explicit set of onboarding steps the frontend considers "active".
 *
 * Use this array — not a raw count — to determine completion. The database
 * may contain deprecated step values (e.g. ADD_FLOORS) from previous releases;
 * filtering against this list ensures those lingering entries are ignored.
 *
 * Safe changes: add a new value.
 * Unsafe changes: remove or rename a value (steps already stored in the DB
 * won't match and will stop counting toward completion).
 */
export var ACTIVE_ONBOARDING_STEPS = [
    'MANAGE_ORGANIZATION',
    'INVITE_ADMINS',
    'ENABLE_INTEGRATIONS',
    'UPLOAD_LOGO',
    'ADD_USERS',
    'CREATE_BUILDINGS',
    'UPLOAD_MAPS',
    'ADD_RESOURCES',
];
/** Derived from ACTIVE_ONBOARDING_STEPS — do not set manually. */
export var TOTAL_ONBOARDING_STEPS = ACTIVE_ONBOARDING_STEPS.length;
